import { useState, useEffect, useMemo, useRef } from "react";
import {
  Layers3,
  Database,
  Link2,
  Plus,
  Code2,
  Download,
  AlertCircle,
  GitBranch,
  Calculator,
} from "lucide-react";
import type {
  ModelDocument,
  Operation,
  DomainObject,
  ModelSnapshot,
  ModelSummary,
  Diagnostic,
  Diagram,
  Relationship,
} from "../../shared/contracts.js";
import {
  createObject,
  uid,
  applyOperations,
  documentOperations,
  DomainError,
  syncDocument,
} from "../../core/index.js";
import {
  exportMongo,
  importMongo,
  generateSemanticSQL,
  validateAnalytical,
} from "../../engineering/index.js";
import { api, downloadText } from "../api.js";
import "./panels.css";

type ViewContext = { representationId?: string; diagramId?: string };
type Aggregation = NonNullable<DomainObject["aggregation"]>;
const fieldKinds = new Set(["attribute", "column", "semanticField"]);
const sourceBinding = (object: DomainObject) =>
  (object.extensions["dataglow.source"] ?? {}) as {
    alias?: string;
    tableName?: string;
    executionContextId?: string;
  };
function requireRepresentation(model: ModelDocument, id?: string) {
  const representation = model.representations.find(
    (r) => r.representationId === (id ?? model.model.activeRepresentation),
  );
  if (!representation)
    throw new DomainError(
      "REPRESENTATION_REQUIRED",
      "Select an existing representation.",
    );
  return representation;
}
function appearanceOperations(
  model: ModelDocument,
  object: DomainObject,
  view: ViewContext,
): Operation[] {
  const existing =
    model.diagrams.find(
      (d) =>
        d.diagramId === view.diagramId &&
        d.representationId === object.representationId,
    ) ??
    model.diagrams.find((d) => d.representationId === object.representationId);
  const position = {
    x: 100 + ((existing?.appearances.length ?? 0) % 3) * 320,
    y: 100 + Math.floor((existing?.appearances.length ?? 0) / 3) * 300,
  };
  const appearance = {
    appearanceId: uid(),
    objectId: object.objectId,
    position,
  };
  if (existing)
    return [
      { type: "appearance.create", diagramId: existing.diagramId, appearance },
    ];
  const diagram: Diagram = {
    diagramId: uid(),
    diagramVersion: 1,
    name: "Main model",
    role: "main",
    representationId: object.representationId,
    perspective: model.model.family,
    notation: "crow-foot",
    appearances: [appearance],
    connectors: [],
  };
  return [{ type: "diagram.create", diagram }];
}
/** Pure, testable source-binding proposal. The server still authorizes the saved source pin. */
export function prepareSourceBinding(
  model: ModelDocument,
  source: ModelSnapshot,
  tableId: string,
  executionContextId: string,
  view: ViewContext & { name?: string } = {},
): { operations: Operation[]; objectId: string } {
  const rep = requireRepresentation(model, view.representationId),
    table = source.document.objects.find((o) => o.objectId === tableId),
    sourceRep = source.document.representations.find(
      (r) => r.representationId === table?.representationId,
    );
  if (model.model.family !== "semantic" || rep.kind !== "semantic")
    throw new DomainError(
      "SEMANTIC_REPRESENTATION_REQUIRED",
      "Bind a source in a semantic representation.",
    );
  if (
    !table ||
    !["table", "view"].includes(table.kind) ||
    sourceRep?.kind !== "physical" ||
    source.document.model.family !== "relational"
  )
    throw new DomainError(
      "PHYSICAL_SOURCE_REQUIRED",
      "Choose a table or view from a saved physical representation. Logical entities do not establish executable SQL bindings.",
    );
  if (!source.commitId || !executionContextId.trim())
    throw new DomainError(
      "SOURCE_PIN_REQUIRED",
      "A saved revision and an execution context are required.",
    );
  const roots = model.objects.filter(
      (o) =>
        o.representationId === rep.representationId &&
        o.kind === "semanticEntity",
    ),
    sourceDialect =
      sourceRep.targetProfile ?? source.document.model.targetProfile;
  if (
    roots.length &&
    sourceDialect &&
    sourceDialect !== model.model.targetProfile
  )
    throw new DomainError(
      "SOURCE_DIALECT_MISMATCH",
      "This source targets another SQL dialect. Use a separate semantic model or a separately reviewed federated execution plan.",
    );
  const name = (view.name || table.displayName || table.name).trim();
  if (!name)
    throw new DomainError("NAME_REQUIRED", "Give the source role a name.");
  const object = createObject("semanticEntity", name, rep.representationId);
  object.description = table.description;
  object.sourceRef = {
    modelId: source.document.model.modelId,
    objectId: tableId,
    commitId: source.commitId,
  };
  let aliasNumber = 1;
  while (roots.some((o) => sourceBinding(o).alias === `s${aliasNumber}`))
    aliasNumber++;
  object.extensions["dataglow.source"] = {
    tableName: table.name,
    schema: table.schema,
    alias: `s${aliasNumber}`,
    executionContextId: executionContextId.trim(),
    commitId: source.commitId,
  };
  const children = source.document.objects
      .filter((c) => c.ownerObjectId === tableId && c.kind === "column")
      .sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0)),
    mapped = new Map<string, string>();
  if (!children.length)
    throw new DomainError(
      "SOURCE_FIELDS_UNAVAILABLE",
      "This source has no projected columns. Define and save the physical table/view output columns before binding it.",
    );
  const fields = children.map((column) => {
    const field = createObject(
      "semanticField",
      `${name}__${column.name}`,
      rep.representationId,
      object.objectId,
    );
    field.displayName = column.displayName || column.name;
    field.description = column.description;
    field.ordinal = column.ordinal;
    field.sourceRef = {
      modelId: source.document.model.modelId,
      objectId: tableId,
      fieldId: column.objectId,
      commitId: source.commitId,
    };
    if (column.type) field.type = structuredClone(column.type);
    field.nullable = column.nullable;
    field.extensions["dataglow.source"] = {
      columnName: column.name,
      commitId: source.commitId,
    };
    mapped.set(column.objectId, field.objectId);
    return field;
  });
  const primary = source.document.objects.find(
    (k) =>
      k.ownerObjectId === tableId &&
      k.kind === "key" &&
      (k.keyRole === "primary" || k.selectedPrimary),
  );
  object.grain = (primary?.members ?? [])
    .map((id) => mapped.get(id))
    .filter((id): id is string => !!id);
  const operations: Operation[] = [
    { type: "object.create", object },
    ...fields.map((object) => ({ type: "object.create", object })),
    ...appearanceOperations(model, object, view),
  ];
  if (
    !roots.length &&
    sourceDialect &&
    sourceDialect !== model.model.targetProfile
  )
    operations.push({
      type: "model.update",
      changes: { targetProfile: sourceDialect },
    });
  applyOperations(model, operations);
  return { operations, objectId: object.objectId };
}
export function prepareMeasure(
  model: ModelDocument,
  ownerId: string,
  name: string,
  aggregation: Aggregation,
  fieldId?: string,
): Operation[] {
  const owner = model.objects.find((o) => o.objectId === ownerId);
  if (!owner || !["semanticEntity", "fact", "dimension"].includes(owner.kind))
    throw new DomainError(
      "MEASURE_OWNER_REQUIRED",
      "Select an analytical or semantic business object.",
    );
  const field = fieldId
    ? model.objects.find(
        (o) =>
          o.objectId === fieldId &&
          o.ownerObjectId === ownerId &&
          o.representationId === owner.representationId &&
          fieldKinds.has(o.kind),
      )
    : undefined;
  if (fieldId && !field)
    throw new DomainError(
      "MEASURE_FIELD_OWNER",
      "Choose a field of the current business object.",
    );
  if (!field && aggregation !== "count")
    throw new DomainError(
      "MEASURE_FIELD_REQUIRED",
      "Only COUNT can use all rows; choose a field for this aggregation.",
    );
  if (!name.trim())
    throw new DomainError("NAME_REQUIRED", "Give the measure a name.");
  const measure = createObject(
    "measure",
    name.trim(),
    owner.representationId,
    ownerId,
  );
  measure.aggregation = aggregation;
  if (field)
    measure.sourceRef = {
      modelId: model.model.modelId,
      objectId: ownerId,
      fieldId: field.objectId,
    };
  else measure.expression = "*";
  return [{ type: "object.create", object: measure }];
}
/** Update the validator and its field projection together, retaining IDs for unchanged paths. */
export function prepareCollectionSchema(
  model: ModelDocument,
  collectionId: string,
  text: string,
): { operations: Operation[]; diagnostics: Diagnostic[] } {
  const collection = model.objects.find(
    (o) => o.objectId === collectionId && o.kind === "collection",
  );
  if (!collection)
    throw new DomainError("COLLECTION_REQUIRED", "Select a collection.");
  const schema = JSON.parse(text);
  if (!schema || Array.isArray(schema) || typeof schema !== "object")
    throw new DomainError(
      "MONGO_SCHEMA_SHAPE",
      "The schema must be a JSON object.",
    );
  const originalExt = (collection.extensions["dataglow.document"] ??
    {}) as Record<string, unknown>;
  const imported = importMongo(
    JSON.stringify({
      name: collection.name,
      validator: {
        ...((originalExt.outerValidator as object) ?? {}),
        $jsonSchema: schema,
      },
      validationLevel: originalExt.validationLevel ?? "strict",
      validationAction: originalExt.validationAction ?? "error",
      indexes: originalExt.indexes ?? [],
    }),
  );
  if (imported.diagnostics.some((d) => d.severity === "error"))
    return { operations: [], diagnostics: imported.diagnostics };
  const importedRoot = imported.document.objects.find(
    (o) => o.kind === "collection",
  );
  if (!importedRoot)
    throw new DomainError(
      "COLLECTION_REQUIRED",
      "The validator did not produce a collection definition.",
    );
  const candidate = structuredClone(model),
    target = candidate.objects.find((o) => o.objectId === collectionId)!;
  target.documentSchema = structuredClone(schema);
  target.extensions = {
    ...target.extensions,
    "dataglow.document": {
      ...originalExt,
      ...(importedRoot.extensions["dataglow.document"] as object),
    },
  };
  const existingPaths = new Map<string, DomainObject>(),
    oldIds = new Set<string>();
  const indexOld = (ownerId: string, path: string[]) => {
    for (const property of model.objects.filter(
      (o) => o.ownerObjectId === ownerId && o.kind === "property",
    )) {
      const next = [...path, property.name];
      existingPaths.set(JSON.stringify(next), property);
      oldIds.add(property.objectId);
      indexOld(property.objectId, next);
    }
  };
  indexOld(collectionId, []);
  const projected: DomainObject[] = [];
  const project = (
    importedOwner: string,
    targetOwner: string,
    path: string[],
  ) => {
    for (const property of imported.document.objects.filter(
      (o) => o.ownerObjectId === importedOwner && o.kind === "property",
    )) {
      const next = [...path, property.name],
        existing = existingPaths.get(JSON.stringify(next));
      const object: DomainObject = {
        ...(existing ? structuredClone(existing) : property),
        objectId: existing?.objectId ?? uid(),
        representationId: collection.representationId,
        ownerObjectId: targetOwner,
        name: property.name,
        type: property.type,
        nullable: property.nullable,
        ordinal: property.ordinal,
        documentSchema: property.documentSchema,
        extensions: { ...existing?.extensions, ...property.extensions },
      };
      projected.push(object);
      project(property.objectId, object.objectId, next);
    }
  };
  project(importedRoot.objectId, collectionId, []);
  candidate.objects = candidate.objects.filter((o) => !oldIds.has(o.objectId));
  candidate.objects.push(...projected);
  syncDocument(candidate);
  const operations = documentOperations(model, candidate);
  applyOperations(model, operations);
  return { operations, diagnostics: imported.diagnostics };
}

export default function PerspectivePanel({
  document: model,
  onOperations,
  readOnly = false,
  representationId: requestedRepresentation,
  diagramId,
}: {
  document: ModelDocument;
  onOperations: (ops: Operation[], summary: string) => void | boolean;
  readOnly?: boolean;
  representationId?: string;
  diagramId?: string;
}) {
  const [sourceModels, setSourceModels] = useState<ModelSummary[]>([]),
    [source, setSource] = useState<ModelSnapshot | null>(null),
    [sourceId, setSourceId] = useState(""),
    [sourceLoading, setSourceLoading] = useState(false),
    [tableId, setTableId] = useState(""),
    [bindingName, setBindingName] = useState(""),
    [context, setContext] = useState("primary"),
    [error, setError] = useState(""),
    [name, setName] = useState(""),
    [selected, setSelected] = useState(""),
    [schemaText, setSchemaText] = useState(""),
    [output, setOutput] = useState(""),
    [diagnostics, setDiagnostics] = useState<Diagnostic[]>([]),
    [measureName, setMeasureName] = useState(""),
    [aggregation, setAggregation] = useState<Aggregation>("count"),
    [measureField, setMeasureField] = useState("");
  const [joinId, setJoinId] = useState(""),
    [joinPairs, setJoinPairs] = useState<{ source: string; target: string }[]>(
      [],
    ),
    [joinType, setJoinType] = useState("inner");
  const schemaBase = useRef({ id: "", text: "" });
  const family = model.model.family,
    representationId =
      requestedRepresentation ?? model.model.activeRepresentation,
    view = { representationId, diagramId };
  const roots = useMemo(
    () =>
      model.objects.filter(
        (o) =>
          o.representationId === representationId &&
          !o.ownerObjectId &&
          (family === "semantic"
            ? o.kind === "semanticEntity"
            : family === "analytical"
              ? ["fact", "dimension"].includes(o.kind)
              : family === "document"
                ? o.kind === "collection"
                : true),
      ),
    [model, representationId, family],
  );
  const active = roots.find((o) => o.objectId === selected) || roots[0],
    fields = active
      ? model.objects.filter(
          (o) =>
            o.ownerObjectId === active.objectId &&
            o.representationId === representationId,
        )
      : [],
    usableFields = fields.filter((f) => fieldKinds.has(f.kind));
  const relations = model.relationships.filter(
      (r) =>
        r.representationId === representationId &&
        roots.some((o) => o.objectId === r.sourceObjectId) &&
        roots.some((o) => o.objectId === r.targetObjectId),
    ),
    join = relations.find((r) => r.relationshipId === joinId) || relations[0];
  const canonicalSchema = JSON.stringify(
      active?.documentSchema ?? { bsonType: "object", properties: {} },
      null,
      2,
    ),
    schemaDirty = family === "document" && schemaText !== canonicalSchema;
  useEffect(() => {
    setSelected("");
    setError("");
    setSourceId("");
    setSource(null);
    setTableId("");
    setBindingName("");
    setJoinId("");
    setMeasureField("");
    setName("");
  }, [model.model.modelId, family, representationId]);
  useEffect(() => {
    setOutput("");
    setDiagnostics([]);
  }, [model, representationId]);
  useEffect(() => {
    setMeasureField("");
    setMeasureName("");
  }, [active?.objectId]);
  useEffect(() => {
    if (family !== "semantic") return;
    let cancelled = false;
    const controller = new AbortController();
    api<ModelSummary[]>("/models", { signal: controller.signal })
      .then((value) => {
        if (!cancelled)
          setSourceModels(
            (Array.isArray(value) ? value : []).filter(
              (m) =>
                m.modelId !== model.model.modelId && m.family === "relational",
            ),
          );
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [family, model.model.modelId]);
  useEffect(() => {
    setSource(null);
    setTableId("");
    setBindingName("");
    if (!sourceId) {
      setSourceLoading(false);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setSourceLoading(true);
    api<ModelSnapshot>(`/models/${encodeURIComponent(sourceId)}`, {
      signal: controller.signal,
    })
      .then((value) => {
        if (!cancelled) setSource(value);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setSourceLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [sourceId]);
  useEffect(() => {
    if (family !== "document") return;
    if (
      schemaBase.current.id === active?.objectId &&
      schemaText !== schemaBase.current.text &&
      canonicalSchema !== schemaText
    ) {
      setError(
        "The collection changed while this validator draft was open. Copy your draft or reload the saved validator before applying it.",
      );
      return;
    }
    setSchemaText(canonicalSchema);
    schemaBase.current = { id: active?.objectId ?? "", text: canonicalSchema };
  }, [active?.objectId, canonicalSchema, family]);
  useEffect(() => {
    setJoinType(String(join?.extensions?.joinType ?? "inner"));
    setJoinPairs(
      join?.sourceAttributeIds?.map((source, index) => ({
        source,
        target: join.targetAttributeIds?.[index] ?? "",
      })) ?? [],
    );
  }, [
    join?.relationshipId,
    JSON.stringify(join?.sourceAttributeIds),
    JSON.stringify(join?.targetAttributeIds),
    join?.extensions?.joinType,
  ]);
  function submit(operations: Operation[], summary: string): boolean {
    if (readOnly) return false;
    try {
      applyOperations(model, operations);
      if (onOperations(operations, summary) === false) return false;
      setError("");
      return true;
    } catch (e) {
      const failure = e as DomainError;
      setError(failure.message);
      if (Array.isArray(failure.details))
        setDiagnostics(failure.details as Diagnostic[]);
      return false;
    }
  }
  function addRoot(kind: DomainObject["kind"]) {
    if (!name.trim()) return;
    const object = createObject(kind, name.trim(), representationId);
    if (kind === "collection")
      object.documentSchema = { bsonType: "object", properties: {} };
    if (kind === "fact") object.grain = [];
    if (
      submit(
        [
          { type: "object.create", object },
          ...appearanceOperations(model, object, view),
        ],
        `Add ${kind}`,
      )
    ) {
      setName("");
      setSelected(object.objectId);
    }
  }
  function bindSource() {
    try {
      if (
        !source ||
        source.document.model.modelId !== sourceId ||
        sourceLoading
      )
        return;
      const proposal = prepareSourceBinding(model, source, tableId, context, {
        ...view,
        name: bindingName,
      });
      if (submit(proposal.operations, "Bind semantic source")) {
        setSelected(proposal.objectId);
        setBindingName("");
      }
    } catch (e) {
      setError((e as Error).message);
    }
  }
  function addMeasure() {
    if (!active) return;
    try {
      if (
        submit(
          prepareMeasure(
            model,
            active.objectId,
            measureName,
            aggregation,
            measureField || undefined,
          ),
          "Add measure",
        )
      )
        setMeasureName("");
    } catch (e) {
      setError((e as Error).message);
    }
  }
  function applyValidator() {
    if (!active) return;
    try {
      if (
        schemaBase.current.id === active.objectId &&
        schemaBase.current.text !== canonicalSchema &&
        schemaText !== canonicalSchema
      )
        throw new Error(
          "The saved validator changed. Reload it and reconcile your draft before applying.",
        );
      const proposal = prepareCollectionSchema(
        model,
        active.objectId,
        schemaText,
      );
      setDiagnostics(proposal.diagnostics);
      if (
        !proposal.diagnostics.some((d) => d.severity === "error") &&
        submit(
          proposal.operations,
          "Update collection validator and property projection",
        )
      ) {
        const formatted = JSON.stringify(JSON.parse(schemaText), null, 2);
        schemaBase.current = { id: active.objectId, text: formatted };
        setSchemaText(formatted);
      }
    } catch (e) {
      setError((e as Error).message);
    }
  }
  function saveJoin() {
    if (!join) return;
    if (!joinPairs.length || joinPairs.some((p) => !p.source || !p.target)) {
      setError("Map at least one ordered field pair before applying the join.");
      return;
    }
    if (
      new Set(joinPairs.map((p) => p.source)).size !== joinPairs.length ||
      new Set(joinPairs.map((p) => p.target)).size !== joinPairs.length
    ) {
      setError("A field can appear only once in each side of the join.");
      return;
    }
    submit(
      [
        {
          type: "relationship.update",
          relationshipId: join.relationshipId,
          changes: {
            sourceAttributeIds: joinPairs.map((p) => p.source),
            targetAttributeIds: joinPairs.map((p) => p.target),
            extensions: { ...join.extensions, joinType },
          },
        },
      ],
      "Set semantic join mapping",
    );
  }
  function generate() {
    try {
      setError("");
      setOutput("");
      const scoped: ModelDocument = {
        ...model,
        model: { ...model.model, activeRepresentation: representationId },
      };
      if (family === "document") {
        const result = exportMongo({
          ...scoped,
          objects: model.objects.filter(
            (o) => o.representationId === representationId,
          ),
        });
        setOutput(result.json);
        setDiagnostics(result.diagnostics);
      } else if (family === "semantic") {
        const result = generateSemanticSQL(scoped);
        setOutput(result.sql);
        setDiagnostics(result.diagnostics);
      } else
        setDiagnostics(
          validateAnalytical({
            ...scoped,
            objects: model.objects.filter(
              (o) => o.representationId === representationId,
            ),
          }),
        );
    } catch (e) {
      setError((e as Error).message);
    }
  }
  const sourceTables =
    source?.document.objects.filter(
      (o) =>
        !o.ownerObjectId &&
        ["table", "view"].includes(o.kind) &&
        source.document.representations.some(
          (r) =>
            r.representationId === o.representationId && r.kind === "physical",
        ),
    ) ?? [];
  return (
    <div className="dgx-perspective" data-testid="perspective-panel">
      <span className="dgx-eyebrow">
        <Layers3 size={15} /> {family.toUpperCase()} PERSPECTIVE
      </span>
      {family === "relational" && (
        <>
          <h2>Meaning across views</h2>
          <p className="dgx-lead">
            Representations share traceable business meaning. Each diagram has
            its own presentation.
          </p>
          <div className="dgx-perspective-cards">
            {model.representations.map((r) => (
              <article key={r.representationId}>
                <span className="dgx-eyebrow">
                  {r.origin}
                  {r.baseline ? " · preserved baseline" : ""}
                </span>
                <h3>{r.name}</h3>
                <p>
                  {r.kind}
                  {r.targetProfile ? ` · ${r.targetProfile}` : ""}
                </p>
                <span>
                  {
                    model.objects.filter(
                      (o) => o.representationId === r.representationId,
                    ).length
                  }{" "}
                  objects
                </span>
                <ul className="dgx-diagram-summary">
                  {model.diagrams.filter((d) => d.representationId === r.representationId).map((d) => <li key={d.diagramId}>
                    <strong>{d.name}</strong>{" · "}{r.baseline ? "Preserved baseline" : d.role === "main" ? "Main model" : d.role === "subject-area" ? "Subject area" : "View"}
                    {d.parentDiagramId && <small>{" · in "}{model.diagrams.find((parent) => parent.diagramId === d.parentDiagramId)?.name}</small>}
                    <small>{" · "}{d.appearances.length} of {model.objects.filter((object) => object.representationId === r.representationId && !object.ownerObjectId).length} entities shown</small>
                  </li>)}
                </ul>
              </article>
            ))}
          </div>
          <h3>
            <GitBranch size={15} /> Transformation mappings
          </h3>
          {model.mappings.length ? (
            model.mappings.slice(0, 30).map((mapping) => (
              <div className="dgx-mapping" key={mapping.mappingId}>
                <strong>{mapping.mappingKind}</strong>
                <span>
                  {mapping.sourceRefs
                    .map(
                      (ref) =>
                        model.objects.find((o) => o.objectId === ref.objectId)
                          ?.name || ref.objectId.slice(0, 8),
                    )
                    .join(", ")}{" "}
                  →{" "}
                  {mapping.destinationRefs
                    .map(
                      (ref) =>
                        model.objects.find((o) => o.objectId === ref.objectId)
                          ?.name || ref.objectId.slice(0, 8),
                    )
                    .join(", ")}
                </span>
                <small>
                  {mapping.reviewState} · {mapping.rationale}
                </small>
              </div>
            ))
          ) : (
            <p className="dgx-muted">
              Use Transform to create a reviewed derived representation and its
              mappings.
            </p>
          )}
        </>
      )}
      {family === "semantic" && (
        <>
          <h2>A business view of your data</h2>
          <p className="dgx-lead">
            Bind fields to saved physical sources, declare grain and review
            joins. Different execution contexts remain design-only.
          </p>
          <details className="dgx-details" open>
            <summary>
              <Database size={15} /> Bind a model source
            </summary>
            <div className="dgx-form">
              <label>
                Saved source model
                <select
                  value={sourceId}
                  onChange={(e) => setSourceId(e.target.value)}
                  disabled={readOnly}
                >
                  <option value="">Select a model</option>
                  {sourceModels.map((m) => (
                    <option key={m.modelId} value={m.modelId}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Physical source table / view
                <select
                  value={tableId}
                  onChange={(e) => {
                    setTableId(e.target.value);
                    const table = sourceTables.find(
                      (o) => o.objectId === e.target.value,
                    );
                    setBindingName(table?.displayName || table?.name || "");
                  }}
                  disabled={!source || sourceLoading || readOnly}
                >
                  <option value="">
                    {sourceLoading
                      ? "Loading saved revision..."
                      : "Select a physical object"}
                  </option>
                  {sourceTables.map((o) => (
                    <option key={o.objectId} value={o.objectId}>
                      {o.schema ? `${o.schema}.` : ""}
                      {o.name} ·{" "}
                      {
                        source?.document.representations.find(
                          (r) => r.representationId === o.representationId,
                        )?.name
                      }
                    </option>
                  ))}
                </select>
                {source && !sourceTables.length && (
                  <small>
                    This model has no physical tables or views. Derive a
                    physical representation first.
                  </small>
                )}
              </label>
              <label>
                Business role name
                <input
                  value={bindingName}
                  onChange={(e) => setBindingName(e.target.value)}
                  disabled={readOnly}
                />
                <small>
                  Use different role names when binding the same source more
                  than once. Output fields receive this role prefix.
                </small>
              </label>
              <label>
                Execution context
                <input
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  disabled={readOnly}
                />
                <small>
                  Use the same name only for sources actually available to one
                  SQL engine.
                </small>
              </label>
              {source && (
                <small>
                  Source revision {source.revision} ·{" "}
                  {source.commitId.slice(0, 8)}. Bindings remain pinned to this
                  saved revision.
                </small>
              )}
              <button
                className="dgx-primary"
                disabled={
                  !source ||
                  sourceLoading ||
                  !tableId ||
                  !bindingName.trim() ||
                  !context.trim() ||
                  readOnly
                }
                onClick={bindSource}
              >
                <Link2 size={15} />
                Bind fields
              </button>
            </div>
          </details>
        </>
      )}
      {family === "semantic" && roots.length > 0 && (
        <label className="dgx-select-label">
          SQL root and result grain
          <select
            disabled={readOnly}
            value={
              roots.some(
                (o) =>
                  o.objectId ===
                  (model.extensions.semanticQuery as any)?.rootObjectId,
              )
                ? String((model.extensions.semanticQuery as any).rootObjectId)
                : roots[0].objectId
            }
            onChange={(e) =>
              submit(
                [
                  {
                    type: "metadata.update",
                    changes: {
                      extensions: {
                        ...model.extensions,
                        semanticQuery: {
                          ...((model.extensions.semanticQuery as object) ?? {}),
                          rootObjectId: e.target.value,
                        },
                      },
                    },
                  },
                ],
                "Select semantic query root",
              )
            }
          >
            {roots.map((root) => (
              <option value={root.objectId} key={root.objectId}>
                {root.name}
              </option>
            ))}
          </select>
          <small>
            Start from the detail-side grain. Joins that multiply this grain
            require a reviewed aggregation plan.
          </small>
        </label>
      )}
      {family === "analytical" && (
        <>
          <h2>Grain before aggregation</h2>
          <p className="dgx-lead">
            Define facts, dimensions and measures. Validate the design before
            selecting an execution adapter.
          </p>
          <div className="dgx-form">
            <label>
              Object name
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Sales fact"
                disabled={readOnly}
              />
            </label>
            <div className="dgx-actions">
              <button
                disabled={!name.trim() || readOnly}
                onClick={() => addRoot("fact")}
              >
                <Plus size={14} />
                Fact
              </button>
              <button
                disabled={!name.trim() || readOnly}
                onClick={() => addRoot("dimension")}
              >
                <Plus size={14} />
                Dimension
              </button>
            </div>
          </div>
        </>
      )}
      {family === "document" && (
        <>
          <h2>Collections with structure</h2>
          <p className="dgx-lead">
            Preserve nested objects, arrays and document validators.
          </p>
          <div className="dgx-inline-form">
            <input
              aria-label="Collection name"
              placeholder="Collection name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={readOnly}
            />
            <button
              aria-label="Add collection"
              className="dgx-primary"
              disabled={!name.trim() || readOnly}
              onClick={() => addRoot("collection")}
            >
              <Plus size={15} />
            </button>
          </div>
          {active && (
            <div className="dgx-form">
              <label>
                Collection
                <select
                  value={active.objectId}
                  onChange={(e) => setSelected(e.target.value)}
                >
                  {roots.map((o) => (
                    <option value={o.objectId} key={o.objectId}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                MongoDB $jsonSchema
                <textarea
                  className="dgx-code-input"
                  rows={18}
                  spellCheck={false}
                  value={schemaText}
                  onChange={(e) => setSchemaText(e.target.value)}
                  readOnly={readOnly}
                />
              </label>
              <small>
                Applying updates the validator and projected property tree
                together. Unchanged property paths keep their identities.
              </small>
              <div className="dgx-actions">
                <button
                  disabled={readOnly || !schemaDirty}
                  onClick={applyValidator}
                >
                  Apply validator
                </button>
                {schemaDirty && (
                  <button
                    onClick={() => {
                      setSchemaText(canonicalSchema);
                      schemaBase.current = {
                        id: active.objectId,
                        text: canonicalSchema,
                      };
                      setError("");
                    }}
                  >
                    Reload saved validator
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}
      {["semantic", "analytical"].includes(family) && active && (
        <>
          <label className="dgx-select-label">
            Business object
            <select
              value={active.objectId}
              onChange={(e) => setSelected(e.target.value)}
            >
              {roots.map((o) => (
                <option key={o.objectId} value={o.objectId}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
          <details className="dgx-details" open>
            <summary>Grain and fields</summary>
            <p className="dgx-muted">
              Select the fields that identify one row of this object.
            </p>
            {usableFields.map((field) => (
              <label className="dgx-grain" key={field.objectId}>
                <input
                  type="checkbox"
                  checked={!!active.grain?.includes(field.objectId)}
                  disabled={readOnly}
                  onChange={(e) =>
                    submit(
                      [
                        {
                          type: "object.update",
                          objectId: active.objectId,
                          changes: {
                            grain: e.target.checked
                              ? [
                                  ...new Set([
                                    ...(active.grain ?? []),
                                    field.objectId,
                                  ]),
                                ]
                              : (active.grain ?? []).filter(
                                  (id) => id !== field.objectId,
                                ),
                          },
                        },
                      ],
                      "Set business grain",
                    )
                  }
                />
                <span>
                  {field.displayName || field.name}
                  <small>
                    {field.sourceRef ? "Pinned source field" : field.kind}
                  </small>
                </span>
              </label>
            ))}
            {!usableFields.length && (
              <p className="dgx-muted">
                Select this object on the canvas, then add fields in Properties.
                A semantic object can obtain fields through source binding.
              </p>
            )}
          </details>
          <details className="dgx-details">
            <summary>
              <Calculator size={15} /> Add a measure
            </summary>
            <div className="dgx-form">
              <label>
                Name
                <input
                  value={measureName}
                  onChange={(e) => setMeasureName(e.target.value)}
                  disabled={readOnly}
                />
              </label>
              <label>
                Aggregation
                <select
                  value={aggregation}
                  onChange={(e) =>
                    setAggregation(e.target.value as Aggregation)
                  }
                  disabled={readOnly}
                >
                  {["count", "sum", "distinct-count", "avg", "min", "max"].map(
                    (value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ),
                  )}
                </select>
              </label>
              <label>
                Field
                <select
                  value={measureField}
                  onChange={(e) => setMeasureField(e.target.value)}
                  disabled={readOnly}
                >
                  <option value="">All rows (COUNT only)</option>
                  {usableFields.map((field) => (
                    <option key={field.objectId} value={field.objectId}>
                      {field.displayName || field.name}
                    </option>
                  ))}
                </select>
              </label>
              <button
                className="dgx-primary"
                disabled={
                  readOnly ||
                  !measureName.trim() ||
                  (!measureField && aggregation !== "count")
                }
                onClick={addMeasure}
              >
                Add measure
              </button>
              {!measureField && aggregation !== "count" && (
                <small>Select a field. Only COUNT supports all rows.</small>
              )}
            </div>
          </details>
          {fields
            .filter((field) => field.kind === "measure")
            .map((field) => (
              <div className="dgx-mapping" key={field.objectId}>
                <strong>{field.name}</strong>
                <span>
                  {field.aggregation}(
                  {field.sourceRef?.fieldId
                    ? model.objects.find(
                        (o) => o.objectId === field.sourceRef!.fieldId,
                      )?.name
                    : "*"}
                  )
                </span>
              </div>
            ))}
        </>
      )}
      {family === "semantic" && join && (
        <details className="dgx-details">
          <summary>
            <Link2 size={15} /> Ordered join fields
          </summary>
          <div className="dgx-form">
            <label>
              Relationship
              <select
                value={join.relationshipId}
                onChange={(e) => setJoinId(e.target.value)}
              >
                {relations.map((relation) => (
                  <option
                    key={relation.relationshipId}
                    value={relation.relationshipId}
                  >
                    {relation.name}
                  </option>
                ))}
              </select>
            </label>
            <small>
              {
                model.objects.find((o) => o.objectId === join.sourceObjectId)
                  ?.name
              }{" "}
              →{" "}
              {
                model.objects.find((o) => o.objectId === join.targetObjectId)
                  ?.name
              }
              . Pairs are evaluated in the displayed order. Cardinality is
              configured in relationship properties.
            </small>
            {joinPairs.map((pair, index) => (
              <div className="dgx-inline-form" key={index}>
                <select
                  aria-label={`Join source field ${index + 1}`}
                  value={pair.source}
                  disabled={readOnly}
                  onChange={(e) =>
                    setJoinPairs(
                      joinPairs.map((p, i) =>
                        i === index ? { ...p, source: e.target.value } : p,
                      ),
                    )
                  }
                >
                  <option value="">Source field</option>
                  {model.objects
                    .filter(
                      (o) =>
                        o.ownerObjectId === join.sourceObjectId &&
                        o.kind === "semanticField",
                    )
                    .map((o) => (
                      <option key={o.objectId} value={o.objectId}>
                        {o.displayName || o.name}
                      </option>
                    ))}
                </select>
                <select
                  aria-label={`Join target field ${index + 1}`}
                  value={pair.target}
                  disabled={readOnly}
                  onChange={(e) =>
                    setJoinPairs(
                      joinPairs.map((p, i) =>
                        i === index ? { ...p, target: e.target.value } : p,
                      ),
                    )
                  }
                >
                  <option value="">Target field</option>
                  {model.objects
                    .filter(
                      (o) =>
                        o.ownerObjectId === join.targetObjectId &&
                        o.kind === "semanticField",
                    )
                    .map((o) => (
                      <option key={o.objectId} value={o.objectId}>
                        {o.displayName || o.name}
                      </option>
                    ))}
                </select>
                <button
                  aria-label={`Remove join field pair ${index + 1}`}
                  disabled={readOnly}
                  onClick={() =>
                    setJoinPairs(joinPairs.filter((_, i) => i !== index))
                  }
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              disabled={readOnly}
              onClick={() =>
                setJoinPairs([...joinPairs, { source: "", target: "" }])
              }
            >
              Add field pair
            </button>
            <label>
              Join behavior
              <select
                value={joinType}
                onChange={(e) => setJoinType(e.target.value)}
                disabled={readOnly}
              >
                <option value="inner">Inner join</option>
                <option value="left">Left join from source</option>
              </select>
            </label>
            <button
              disabled={
                readOnly ||
                !joinPairs.length ||
                joinPairs.some((p) => !p.source || !p.target)
              }
              onClick={saveJoin}
            >
              Apply join mapping
            </button>
          </div>
        </details>
      )}
      {["semantic", "analytical", "document"].includes(family) && (
        <div className="dgx-actions">
          <button
            className="dgx-primary"
            disabled={family === "document" && schemaDirty}
            title={
              family === "document" && schemaDirty
                ? "Apply or reload the validator draft before generating."
                : undefined
            }
            onClick={generate}
          >
            <Code2 size={15} />
            {family === "analytical"
              ? "Validate analytical design"
              : family === "document"
                ? "Generate validator JSON"
                : "Generate SQL query"}
          </button>
          {output && (
            <button
              onClick={() =>
                downloadText(
                  `${model.model.name}.${family === "document" ? "json" : "sql"}`,
                  output,
                  family === "document" ? "application/json" : "text/plain",
                )
              }
            >
              <Download size={15} />
              Download
            </button>
          )}
        </div>
      )}
      {error && (
        <p className="dgx-error" role="alert">
          <AlertCircle size={15} />
          {error}
        </p>
      )}
      {diagnostics.map((diagnostic, index) => (
        <div key={index} className={`dgx-diagnostic ${diagnostic.severity}`}>
          <AlertCircle size={14} />
          <span>
            <strong>{diagnostic.code}</strong>
            {diagnostic.message}
          </span>
        </div>
      ))}
      {output && <pre className="dgx-output">{output}</pre>}
      {["graph", "process"].includes(family) && (
        <div className="dgx-callout">
          <h3>Extension family</h3>
          <p>
            This family is retained in the specification. Its specialized editor
            and execution adapters are not available in this candidate. Use
            Capabilities to inspect the implementation status.
          </p>
        </div>
      )}
    </div>
  );
}
